const HOVER_MQ = window.matchMedia('(hover: hover) and (pointer: fine)');

class PdpIngredientsFormula extends HTMLElement {
  #controller = new AbortController();
  #tabs = [];
  #panels = new Map();
  #mediaSlots = new Map();
  #rows = [];
  #activeRow = null;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    for (const panel of this.querySelectorAll('[role="tabpanel"]')) {
      this.#panels.set(panel.dataset.panel, panel);
    }
    for (const media of this.querySelectorAll('.pdp-ingredients-formula__media[data-panel]')) {
      this.#mediaSlots.set(media.dataset.panel, media);
    }
    this.#rows = Array.from(this.querySelectorAll('[data-ingredient-row]'));

    for (const tab of this.#tabs) {
      tab.addEventListener('click', this.#onTabClick, { signal });
      tab.addEventListener('keydown', this.#onTabKeydown, { signal });
    }

    for (const row of this.#rows) {
      row.addEventListener('click', this.#onRowClick, { signal });
      row.addEventListener('keydown', this.#onRowKeydown, { signal });
      if (HOVER_MQ.matches) {
        row.addEventListener('pointerenter', this.#onRowEnter, { signal });
        row.addEventListener('pointerleave', this.#onRowLeave, { signal });
      }
    }

    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener('keydown', this.#onDocumentKeydown, { signal });

    const firstRow = this.#rows[0];
    if (firstRow) this.#expandRow(firstRow);
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #onTabClick = (event) => {
    const tab = event.currentTarget;
    this.#activateTab(tab);
  };

  #onTabKeydown = (event) => {
    const currentIndex = this.#tabs.indexOf(event.currentTarget);
    if (currentIndex === -1) return;
    let nextIndex = currentIndex;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const nextTab = this.#tabs[nextIndex];
    this.#activateTab(nextTab);
    nextTab.focus();
  };

  #activateTab(tab) {
    if (!tab || tab.classList.contains('is-active')) return;
    const target = tab.dataset.tabTarget;
    const nextPanel = this.#panels.get(target);
    if (!nextPanel) return;

    for (const t of this.#tabs) {
      const isActive = t === tab;
      t.classList.toggle('is-active', isActive);
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
      t.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    const currentPanel = Array.from(this.#panels.values()).find(
      (p) => !p.hidden && p !== nextPanel
    );
    const currentMedia = currentPanel ? this.#mediaSlots.get(currentPanel.dataset.panel) : null;
    const nextMedia = this.#mediaSlots.get(target);

    if (currentPanel) {
      currentPanel.classList.add('is-fading');
      if (currentMedia) currentMedia.classList.add('is-fading');
      const onEnd = () => {
        currentPanel.hidden = true;
        currentPanel.classList.remove('is-fading', 'is-active');
        if (currentMedia) {
          currentMedia.hidden = true;
          currentMedia.classList.remove('is-fading', 'is-active');
        }
        currentPanel.removeEventListener('transitionend', onEnd);
        this.#showPanel(nextPanel, nextMedia);
      };
      currentPanel.addEventListener('transitionend', onEnd, { once: true });
      requestAnimationFrame(() => {
        if (currentPanel.classList.contains('is-fading') && !currentPanel.hidden) {
          setTimeout(() => {
            if (currentPanel.classList.contains('is-fading')) {
              onEnd();
            }
          }, 350);
        }
      });
    } else {
      this.#showPanel(nextPanel, nextMedia);
    }
  }

  #showPanel(panel, media) {
    panel.hidden = false;
    panel.classList.add('is-fading');
    if (media) {
      media.hidden = false;
      media.classList.add('is-fading');
    }
    requestAnimationFrame(() => {
      panel.classList.remove('is-fading');
      panel.classList.add('is-active');
      if (media) {
        media.classList.remove('is-fading');
        media.classList.add('is-active');
      }
    });
    this.#rows = Array.from(this.querySelectorAll('[data-ingredient-row]'));
    this.#activeRow = null;
    const firstRow = panel.querySelector('[data-ingredient-row]');
    if (firstRow) this.#expandRow(firstRow);
  }

  #onRowClick = (event) => {
    const row = event.currentTarget;
    if (this.#activeRow === row) {
      if (HOVER_MQ.matches) return;
      this.#collapseRow(row);
      return;
    }
    this.#expandRow(row);
  };

  #onRowKeydown = (event) => {
    const row = event.currentTarget;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (this.#activeRow === row) {
        this.#collapseRow(row);
      } else {
        this.#expandRow(row);
      }
    } else if (event.key === 'Escape' && this.#activeRow === row) {
      this.#collapseRow(row);
    }
  };

  #onRowEnter = (event) => {
    if (!HOVER_MQ.matches) return;
    const row = event.currentTarget;
    this.#expandRow(row);
  };

  #onRowLeave = (event) => {
    if (!HOVER_MQ.matches) return;
    const row = event.currentTarget;
    if (row.contains(document.activeElement)) return;
  };

  #onDocumentClick = (event) => {
    if (!this.#activeRow) return;
    if (this.contains(event.target)) return;
    this.#collapseRow(this.#activeRow);
  };

  #onDocumentKeydown = (event) => {
    if (event.key === 'Escape' && this.#activeRow) {
      this.#collapseRow(this.#activeRow);
      this.#activeRow.focus();
    }
  };

  #expandRow(row) {
    if (this.#activeRow && this.#activeRow !== row) {
      this.#activeRow.classList.remove('is-active');
      this.#activeRow.setAttribute('aria-expanded', 'false');
    }
    row.classList.add('is-active');
    row.setAttribute('aria-expanded', 'true');
    this.#activeRow = row;
  }

  #collapseRow(row) {
    row.classList.remove('is-active');
    row.setAttribute('aria-expanded', 'false');
    if (this.#activeRow === row) this.#activeRow = null;
  }
}

if (!customElements.get('pdp-ingredients-formula')) {
  customElements.define('pdp-ingredients-formula', PdpIngredientsFormula);
}
